import MyDialog from "~/mui/MyDialog";
import { checkForErrorsState, formateDateAndTime, getStrong } from '~/utils/utils';
import { useTrans } from '~/hooks/useTrans';
import { apiCreateProjectAdminComment, apiEditProjectAdminComment } from '~/providers/api';
import { IAdminComment } from '~/providers/admin-interfaces';
import { IUrlParams } from "~/interfaces";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import Wysiwyg, { contentToHTML, htmlToContent, IWysiwygContent } from "~/components/Wysiwyg";

import "./style.less";

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
}

interface IStateFormData {
	bodyCs: null | IWysiwygContent;
}

// chyby formulare
type TStateErrors = {[key in keyof Pick<IStateFormData, "bodyCs">]: string; };

interface IAdminCommentModal {
	params: IUrlParams;
	commentData: IAdminComment;
	onSave?: (comment: IAdminComment) => void;
	onClose?: () => void;
	onChange?: (isChanged: boolean) => void;
}

export default function AdminCommentModal({
	params = null,
	commentData = null,
	onSave = () => {},
	onClose = () => {},
	onChange = () => {},
}: IAdminCommentModal) {
	const { trans } = useTrans();
	const { lang, isCzech } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
	});
	const { state: stateFormData, updateState: updateFormData } = myUseState<IStateFormData>({
		bodyCs: commentData
			? htmlToContent(commentData.comment)
			: null,
	});
	const { state: stateError, setState: setStateError } = myUseState<TStateErrors>({
		bodyCs: "",
	});
	const isEdit = commentData !== null;

	function getCommentInfo() {
		return trans("projectBusinessPage.lastComment", {
			user: getStrong(commentData.ownerUserEmail),
			date: getStrong(formateDateAndTime(new Date(commentData.updatedAt), isCzech, true)),
		});
	}

	function getWasChange(newState: Partial<IStateFormData>): boolean {
		const newFormDataState = {
			...stateFormData,
			...newState,
		};
		const hasBodyCs = newFormDataState.bodyCs && newFormDataState.bodyCs.getPlainText() !== "";

		return hasBodyCs;
	}

	function updateFormDataWithChange(formDataState: Partial<IStateFormData>, stateData?: Partial<IState>) {
		const wasChanged = getWasChange(formDataState);

		updateFormData(formDataState);
		updateState({
			wasChanged,
			...stateData,
		});
		onChange(wasChanged);
	}

	async function dialogOnSave() {
		const formData: TStateErrors = {
			bodyCs: stateFormData.bodyCs?.getPlainText() || "",
		};
		const errorData = checkForErrorsState<TStateErrors, TStateErrors>(formData, {
			bodyCs: { check: "string-gt5", msg: trans("projectSettingsModal.tabs.error.emptyBody") },
		});

		setStateError(errorData.errorsState);

		// jsou tam chyby
		if (errorData.errorsCount > 0) {
			return;
		}

		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		let errors = [];
		let newComment: IAdminComment = null;

		if (isEdit) {
			const editData = await apiEditProjectAdminComment(params.projectId, commentData.id, {
				comment: contentToHTML(stateFormData.bodyCs),
			}, lang);

			if (editData.noErrors) {
				newComment = editData.data;
			} else {
				errors = editData.errors;
			}
		} else {
			const newData = await apiCreateProjectAdminComment(params.projectId, {
				comment: contentToHTML(stateFormData.bodyCs),
			}, lang);

			if (newData.noErrors) {
				newComment = newData.data;
			} else {
				errors = newData.errors;
			}
		}

		if (errors.length > 0) {
			updateState({
				dialogLoading: false,
				errorMsg: errors.join(", "),
			});
		} else {
			onSave(newComment);
		}
	}

	return <MyDialog title={trans(isEdit ? "adminCommentModal.editTitle" : "adminCommentModal.newTitle")} onClose={onClose} onSave={dialogOnSave} className="adminCommentModal" dialogLoading={state.dialogLoading}
		disableSave={!state.wasChanged} errorMsg={state.errorMsg} confirmClose={state.wasChanged} minWidth="550px">
		<div>
			<Wysiwyg title={trans("adminCommentModal.bodyCs")} content={stateFormData.bodyCs} error={stateError.bodyCs} onChange={content => updateFormDataWithChange({ bodyCs: content })} />
		</div>
		{ commentData && <p dangerouslySetInnerHTML={{ __html: getCommentInfo() }} /> }
	</MyDialog>;
}
